// @/src/core/services/clientes.service.ts
import { db, storage } from '@/core/config/firebase';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  FieldValue,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  CLIENTES_COLLECTION_NAME,
  ACTIVIDADES_CLIENTE_SUBCOLLECTION_NAME
} from '@/core/constants/collections';
import type { Cliente, ActividadCliente, UsuarioSistema } from '@/core/types';
import { authStore } from '@/core/store/auth.store';

/**
 * Obtiene todos los clientes de un usuario aplicando filtros opcionales.
 * @param userId - ID del usuario propietario de los clientes.
 * @param filtros - Opciones de filtrado por estado o propietario.
 * @returns Una promesa que resuelve a un array de clientes.
 */
export async function getClientes(
  userId: string,
  filtros: { estado?: string; propietarioId?: string; busqueda?: string } = {}
): Promise<Cliente[]> {
  if (!userId) {
    throw new Error('Se requiere un ID de usuario para obtener los clientes.');
  }

  const clientesRef = collection(db, `usuarios/${userId}/${CLIENTES_COLLECTION_NAME}`);
  
  // Estrategia: Para evitar índices compuestos costosos, hacemos filtrado local cuando hay filtros
  // que requieren combinación con orderBy. Solo usamos índices simples en Firestore.
  let q = query(clientesRef, orderBy('timestampActualizacion', 'desc'));
  let necesitaFiltradoLocal = false;

  // La búsqueda por texto tiene prioridad y no se combina con otros filtros en la misma consulta de Firestore
  // para evitar la necesidad de crear un índice compuesto para cada combinación.
  if (filtros.busqueda) {
    // Buscar por el término de búsqueda más específico (el más largo que pueda encontrar coincidencias)
    const terminos = filtros.busqueda.toLowerCase().split(/\s+/).filter(Boolean);
    if (terminos.length > 0) {
      // Usar el primer término como base para la consulta de Firestore
      const primerTermino = terminos[0];
      q = query(q, where('terminosBusqueda', 'array-contains', primerTermino));
      console.log(`[ClientesService] Buscando por término: "${primerTermino}" de la búsqueda: "${filtros.busqueda}"`);
      // Si hay búsqueda + otros filtros, necesitamos filtrado local
      if (filtros.estado || filtros.propietarioId) {
        necesitaFiltradoLocal = true;
      }
    }
  } else {
    // Sin búsqueda: aplicar filtros directamente en Firestore si no requieren índice compuesto
    // Si hay múltiples filtros o filtro de estado, hacer filtrado local para evitar índices compuestos
    if (filtros.estado && filtros.propietarioId) {
      // Múltiples filtros: usar solo uno en Firestore y filtrar localmente el otro
      q = query(q, where('estadoCliente', '==', filtros.estado));
      necesitaFiltradoLocal = true;
    } else if (filtros.estado) {
      // Solo estado: hacer filtrado local para evitar índice compuesto con orderBy
      necesitaFiltradoLocal = true;
    } else if (filtros.propietarioId) {
      // Solo propietario: usar índice existente si está disponible, sino filtrado local
      q = query(q, where('propietarioId', '==', filtros.propietarioId));
    }
  }

  console.log(`[ClientesService] Ejecutando consulta en Firestore...`);
  const querySnapshot = await getDocs(q);
  
  let clientes: Cliente[] = [];
  querySnapshot.forEach((doc) => {
    clientes.push({ id: doc.id, ...doc.data() } as Cliente);
  });

  console.log(`[ClientesService] ✅ Firestore devolvió ${clientes.length} clientes de la consulta inicial`);

  // Aplicar filtrado local si es necesario (para evitar índices compuestos)
  if (necesitaFiltradoLocal) {
    const clientesAntes = clientes.length;
    if (filtros.estado) {
      clientes = clientes.filter(c => c.estadoCliente === filtros.estado);
      console.log(`[ClientesService] 🔍 Filtrado local por estado "${filtros.estado}": ${clientesAntes} → ${clientes.length} clientes`);
    }
    if (filtros.propietarioId) {
      clientes = clientes.filter(c => c.propietarioId === filtros.propietarioId);
      console.log(`[ClientesService] 🔍 Filtrado local por propietario "${filtros.propietarioId}": ${clientes.length} clientes`);
    }
  }

  // Si hay búsqueda, filtrar adicionalmente por múltiples términos
  if (filtros.busqueda) {
    const terminos = filtros.busqueda.toLowerCase().split(/\s+/).filter(Boolean);
    console.log(`[ClientesService] 🔍 Filtrando ${clientes.length} clientes por términos:`, terminos);
    
    // Debug: verificar algunos clientes antes del filtro
    if (clientes.length > 0) {
      console.log('[ClientesService] 📋 Ejemplo de términos antes del filtro:');
      clientes.slice(0, 2).forEach((cliente, index) => {
        const nombre = cliente.tipoCliente === 'cliente' 
          ? `${cliente.contactos[0]?.nombre || ''} ${cliente.contactos[0]?.apellido || ''}`.trim()
          : cliente.datosEntidad?.nombre;
        console.log(`    ${index + 1}. "${nombre}": [${cliente.terminosBusqueda?.slice(0, 5).join(', ') || 'SIN TÉRMINOS'}]`);
      });
    }
    
    clientes = clientes.filter(cliente => {
      const coincide = terminos.every(termino => 
        cliente.terminosBusqueda?.some(tb => tb.includes(termino))
      );
      
      if (!coincide && clientes.length <= 5) {
        // Solo mostrar detalles si hay pocos clientes para no spam el log
        const nombre = cliente.tipoCliente === 'cliente' 
          ? `${cliente.contactos[0]?.nombre || ''} ${cliente.contactos[0]?.apellido || ''}`.trim()
          : cliente.datosEntidad?.nombre;
        console.log(`    ❌ "${nombre}" no coincide. Términos: [${cliente.terminosBusqueda?.slice(0, 5).join(', ') || 'SIN TÉRMINOS'}]`);
      }
      
      return coincide;
    });
    
    console.log(`[ClientesService] ✅ Después del filtro local: ${clientes.length} clientes`);
  }

  return clientes;
}

/**
 * Sube la foto de un cliente a Firebase Storage y devuelve la URL.
 * @param userId - ID del usuario.
 * @param clienteId - ID del cliente.
 * @param fotoFile - Archivo de imagen para subir.
 * @param oldFotoURL - URL de la foto anterior para borrarla (opcional).
 * @returns La URL de la foto subida.
 */
export async function uploadFotoCliente(userId: string, clienteId: string, fotoFile: File, oldFotoURL?: string): Promise<string> {
    if (oldFotoURL && oldFotoURL.includes('firebasestorage.googleapis.com')) {
        try {
            const oldFileRef = ref(storage, oldFotoURL);
            await deleteObject(oldFileRef);
        } catch (error) {
            console.warn("No se pudo borrar la foto anterior:", error);
        }
    }
    const filePath = `usuarios/${userId}/clientes_fotos/${clienteId}/${fotoFile.name}`;
    const fileRef = ref(storage, filePath);
    const snapshot = await uploadBytes(fileRef, fotoFile);
    return getDownloadURL(snapshot.ref);
}

/**
 * Guarda (crea o actualiza) un cliente en la base de datos.
 * @param userId - ID del usuario.
 * @param clienteData - Datos del cliente a guardar (debe incluir ID).
 * @param esEdicion - Booleano que indica si es una actualización.
 * @returns El ID del cliente guardado.
 */
export async function saveCliente(userId: string, clienteData: Omit<Cliente, 'timestampCreacion' | 'timestampActualizacion' | 'terminosBusqueda'> & { timestampCreacion?: FieldValue, timestampActualizacion?: FieldValue }, esEdicion: boolean): Promise<string> {
    const clienteDocRef = doc(db, `usuarios/${userId}/${CLIENTES_COLLECTION_NAME}`, clienteData.id);

    const contactos = clienteData.contactos ?? [];
    const nombrePrincipal = clienteData.tipoCliente === 'cliente'
        ? `${contactos[0]?.nombre || ''} ${contactos[0]?.apellido || ''}`.trim()
        : clienteData.datosEntidad?.nombre?.trim() || '';

    if (!nombrePrincipal) {
        throw new Error('El cliente debe tener un nombre o razón social antes de guardarse.');
    }

    const telefonosEntidad = clienteData.datosEntidad?.telefonos ?? [];
    const telefonosContactos = contactos.flatMap(contacto => contacto.telefonos ?? []);
    const telefonosNormalizados = [...telefonosEntidad, ...telefonosContactos]
        .map(tel => tel.trim())
        .filter(Boolean);

    if (telefonosNormalizados.length === 0) {
        throw new Error('El cliente debe tener al menos un número de teléfono registrado.');
    }
    
    // Asegura que la fecha de ingreso sea un Timestamp de Firestore para consistencia.
    let fechaIngresoFinal: Timestamp | FieldValue | string = clienteData.fechaIngreso;
    if (typeof clienteData.fechaIngreso === 'string') {
        const fechaConZonaHoraria = clienteData.fechaIngreso + 'T00:00:00-06:00'; // Guatemala Time (GMT-6)
        fechaIngresoFinal = Timestamp.fromDate(new Date(fechaConZonaHoraria));
    } else if (!clienteData.fechaIngreso) {
        fechaIngresoFinal = serverTimestamp();
    }

    const terminosBusqueda = generarTerminosBusqueda(clienteData);

    const dataToSave: typeof clienteData & { terminosBusqueda: string[] } = {
        ...clienteData,
        fechaIngreso: fechaIngresoFinal,
        timestampActualizacion: serverTimestamp(),
        terminosBusqueda,
    };

    if (!esEdicion) {
        dataToSave.timestampCreacion = serverTimestamp();
    }
    
    await setDoc(clienteDocRef, dataToSave, { merge: true });
    
    await registrarActividadCliente(userId, clienteData.id, {
        contenido: esEdicion ? 'Se actualizaron los datos del cliente.' : 'Se creó un nuevo cliente.',
        tipoActividad: esEdicion ? 'actualizacion_cliente' : 'creacion_cliente',
        creadorId: userId,
        creadorNombre: authStore.getState().user?.nombre
    });

    return clienteData.id;
}

/**
 * Genera un array de términos de búsqueda a partir de los datos de un cliente.
 * @param cliente - Datos del cliente.
 * @returns Array de strings en minúsculas para búsqueda.
 */
function generarTerminosBusqueda(cliente: Omit<Cliente, 'timestampCreacion' | 'timestampActualizacion' | 'terminosBusqueda'>): string[] {
    const terminos = new Set<string>();

    // Función auxiliar para normalizar texto (remover acentos)
    const normalizarTexto = (texto: string): string => {
        return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    };

    const agregarConPrefijos = (valor: string | null | undefined) => {
        if (!valor) return;
        const palabras = valor.toLowerCase().split(/\s+/).filter(Boolean);
        palabras.forEach(palabra => {
            if (palabra.length > 20) return; // Evitar terminos muy largos
            
            // Agregar prefijos de la palabra original
            for (let i = 1; i <= palabra.length; i++) {
                terminos.add(palabra.substring(0, i));
            }
            
            // También agregar versión sin acentos si es diferente
            const palabraSinAcentos = normalizarTexto(palabra);
            if (palabraSinAcentos !== palabra) {
                for (let i = 1; i <= palabraSinAcentos.length; i++) {
                    terminos.add(palabraSinAcentos.substring(0, i));
                }
            }
            
            // Para palabras con números, también agregar solo la parte numérica
            const numerosEnPalabra = palabra.match(/\d+/g);
            if (numerosEnPalabra) {
                numerosEnPalabra.forEach(num => {
                    for (let i = 1; i <= num.length; i++) {
                        terminos.add(num.substring(0, i));
                    }
                });
            }
            
            // Para palabras con caracteres especiales, agregar versión sin caracteres especiales
            const palabraSinEspeciales = palabra.replace(/[^\w\s]/g, '');
            if (palabraSinEspeciales !== palabra && palabraSinEspeciales.length > 0) {
                for (let i = 1; i <= palabraSinEspeciales.length; i++) {
                    terminos.add(palabraSinEspeciales.substring(0, i));
                }
            }
        });
    };

    const agregarSinSplitConPrefijos = (valor: string | null | undefined) => {
        if (!valor) return;
        const termino = valor.toLowerCase();
        if (termino.length > 35) return; // Límite para emails o frases largas
        for (let i = 1; i <= termino.length; i++) {
            terminos.add(termino.substring(0, i));
        }
    };

    // Datos de la entidad (si existe)
    if (cliente.datosEntidad) {
        agregarConPrefijos(cliente.datosEntidad.nombre);
        agregarSinSplitConPrefijos(cliente.datosEntidad.email);
        cliente.datosEntidad.telefonos?.forEach(tel => agregarSinSplitConPrefijos(tel.replace(/\D/g, '')));
    }

    // Datos de los contactos
    cliente.contactos.forEach(contacto => {
        agregarConPrefijos(contacto.nombre);
        agregarConPrefijos(contacto.apellido);
        if (contacto.nombre && contacto.apellido) {
            agregarSinSplitConPrefijos(`${contacto.nombre} ${contacto.apellido}`);
            // También agregar nombre apellido sin espacio para búsquedas continuas
            agregarSinSplitConPrefijos(`${contacto.nombre}${contacto.apellido}`);
        }
        agregarSinSplitConPrefijos(contacto.email);
        contacto.telefonos.forEach(tel => {
            const telefonoLimpio = tel.replace(/\D/g, '');
            agregarSinSplitConPrefijos(telefonoLimpio);
            // También agregar el teléfono con formato original para búsquedas con guiones, etc.
            agregarSinSplitConPrefijos(tel.toLowerCase());
        });
    });

    const terminosArray = Array.from(terminos);
    console.log(`[ClientesService] Generados ${terminosArray.length} términos de búsqueda para cliente:`, terminosArray.slice(0, 10), '...');
    
    // Verificar que hay términos válidos
    if (terminosArray.length === 0) {
        console.warn('[ClientesService] ⚠️ No se generaron términos de búsqueda para el cliente');
        // Agregar al menos un término por defecto basado en el tipo
        terminosArray.push(cliente.tipoCliente);
    }
    
    return terminosArray;
}

/**
 * Elimina uno o más clientes y sus fotos asociadas.
 * Ahorra costos al no leer documentos, esperando la fotoURL del estado local.
 * @param userId - ID del usuario.
 * @param clientesParaEliminar - Array de objetos cliente con id y fotoURL.
 */
export async function deleteClientes(userId: string, clientesParaEliminar: Pick<Cliente, 'id' | 'fotoURL'>[]): Promise<void> {
  const batch = writeBatch(db);

  for (const cliente of clientesParaEliminar) {
    // Eliminar foto de Storage si existe
    if (cliente.fotoURL && cliente.fotoURL.includes('firebasestorage.googleapis.com')) {
      try {
        const fotoRef = ref(storage, cliente.fotoURL);
        await deleteObject(fotoRef);
      } catch (error) {
        console.warn(`No se pudo borrar la foto para el cliente ${cliente.id}:`, error);
      }
    }
    // Añadir la eliminación del documento al batch
    const clienteDocRef = doc(db, `usuarios/${userId}/${CLIENTES_COLLECTION_NAME}`, cliente.id);
    batch.delete(clienteDocRef);
  }

  await batch.commit();
}

/**
 * Actualiza en lote el propietario de varios clientes y registra la actividad.
 * @param userId - ID del usuario que realiza la acción.
 * @param clienteIds - IDs de los clientes a actualizar.
 * @param nuevoPropietarioId - ID del nuevo propietario.
 * @param nuevoPropietarioNombre - Nombre del nuevo propietario.
 */
export async function cambiarPropietarioClientes(userId: string, clienteIds: string[], nuevoPropietarioId: string, nuevoPropietarioNombre: string): Promise<void> {
    const batch = writeBatch(db);
    const dataToUpdate = {
        propietarioId: nuevoPropietarioId,
        propietarioNombre: nuevoPropietarioNombre,
        timestampActualizacion: serverTimestamp()
    };
    
    clienteIds.forEach(clienteId => {
        const clienteRef = doc(db, `usuarios/${userId}/${CLIENTES_COLLECTION_NAME}`, clienteId);
        batch.update(clienteRef, dataToUpdate);

        registrarActividadCliente(userId, clienteId, {
            contenido: `Propietario cambiado a ${nuevoPropietarioNombre}.`,
            tipoActividad: 'cambio_propietario',
            creadorId: userId,
            creadorNombre: authStore.getState().user?.nombre
        });
    });

    await batch.commit();
}

/**
 * Actualiza en lote el estado de varios clientes y registra la actividad.
 * @param userId - ID del usuario que realiza la acción.
 * @param clienteIds - IDs de los clientes a actualizar.
 * @param nuevoEstado - Nuevo estado a asignar.
 */
export async function cambiarEstadoClientes(userId: string, clienteIds: string[], nuevoEstado: string): Promise<void> {
    const batch = writeBatch(db);
    const dataToUpdate = { 
        estadoCliente: nuevoEstado,
        timestampActualizacion: serverTimestamp() 
    };

    clienteIds.forEach(clienteId => {
        const clienteRef = doc(db, `usuarios/${userId}/${CLIENTES_COLLECTION_NAME}`, clienteId);
        batch.update(clienteRef, dataToUpdate);

        registrarActividadCliente(userId, clienteId, {
            contenido: `Estado cambiado a ${nuevoEstado}.`,
            tipoActividad: 'cambio_estado',
            creadorId: userId,
            creadorNombre: authStore.getState().user?.nombre
        });
    });

    await batch.commit();
}

/**
 * Obtiene todos los usuarios del sistema para usarlos en filtros.
 * @returns Un array de usuarios con id y nombre.
 */
export async function getSystemUsers(): Promise<UsuarioSistema[]> {
    const { user } = authStore.getState();
    if (!user) {
        console.warn("No hay usuario autenticado para obtener datos de propietario.");
        return [];
    }
    // Devuelve un array con únicamente el usuario actual.
    return [{ id: user.uid, ...user }];
}

/**
 * Registra una nueva actividad en la línea de tiempo de un cliente.
 * @param userId - ID del usuario.
 * @param clienteId - ID del cliente.
 * @param actividadData - Datos de la actividad a registrar.
 */
export async function registrarActividadCliente(userId: string, clienteId: string, actividadData: Omit<ActividadCliente, 'id' | 'timestampCreacion'>): Promise<void> {
    const actividad = {
        ...actividadData,
        timestampCreacion: serverTimestamp()
    };
    const actividadesRef = collection(db, `usuarios/${userId}/${CLIENTES_COLLECTION_NAME}/${clienteId}/${ACTIVIDADES_CLIENTE_SUBCOLLECTION_NAME}`);
    await addDoc(actividadesRef, actividad);
}

/**
 * Obtiene la última actividad registrada para un cliente.
 * @param userId - ID del usuario.
 * @param clienteId - ID del cliente.
 * @returns La última actividad o null si no hay ninguna.
 */
export async function getUltimaActividadCliente(userId: string, clienteId: string): Promise<ActividadCliente | null> {
    const actividadesRef = collection(db, `usuarios/${userId}/${CLIENTES_COLLECTION_NAME}/${clienteId}/${ACTIVIDADES_CLIENTE_SUBCOLLECTION_NAME}`);
    const q = query(actividadesRef, orderBy("timestampCreacion", "desc"), limit(1));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as ActividadCliente;
    }
    return null;
}
